import { ClaimsOverTimeChart } from "@/components/charts/claimCharts/ClaimsOverTimeChart";
import { MetricCardsGrid, MetricCard } from "@/components/common/MetricCards";
import { TabContent } from "@/components/common/PageLayout";
import { CompactTable } from "@/components/common/CompactTable";
import { stoplossClaimColumns, reimbursementColumns } from "@/components/common/reportingColumns";
import { SectionCard } from "@/components/common/SectionCard";
import { ClaimItem, Client, Reimbursement, ResourceName, StoplossClaim, Claimant } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { Grid2 as Grid, Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { useRecordContext, useGetList, useGetMany } from "react-admin";
import {
  IN_PROCESS_STATUSES,
  STOPLOSS_STATUS_GROUP_LABELS,
  SUBMITTED_STATUSES,
} from "@/constants/stoplossClaimsGroupings";
import { getResourceIcon } from "@/utils/icons";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useSelectedPolicyId } from "@/resources/claimants/components/PolicyContext";

export const ClientReportingTab = ({ drawerExpanded }: { drawerExpanded: boolean }) => {
  const clientRecord = useRecordContext<Client>();
  const selectedPolicyId = useSelectedPolicyId();

  // Check permissions for linked resources
  const { canList: canListClaimants } = useResourcePermissions(ResourceName.CLAIMANTS);
  const { canList: canListClaims } = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);
  const { canList: canListReimbursements } = useResourcePermissions(ResourceName.REIMBURSEMENTS);
  const { canList: canListStoplossClaims } = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);

  const ReimbursementIcon = getResourceIcon(ResourceName.REIMBURSEMENTS);
  const StopLossClaimsIcon = getResourceIcon(ResourceName.STOPLOSS_CLAIMS);

  // Build filters — scope to selected policy if one is chosen
  const baseFilter: Record<string, string> = { client_id_ref: clientRecord?.id || "" };
  if (selectedPolicyId) baseFilter.policy_id_ref = selectedPolicyId;

  const ClaimantsIcon = getResourceIcon(ResourceName.CLAIMANTS);

  // Total claimants count
  const { total: totalClaimants } = useGetList<Claimant>(ResourceName.CLAIMANTS, {
    pagination: { page: 1, perPage: 1 },
    filter: selectedPolicyId ? { ...baseFilter, policy_id: selectedPolicyId } : baseFilter,
  });

  const { data: stoplossClaims = [], isLoading: claimsLoading } = useGetList<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: "created_datetime", order: "DESC" },
      filter: baseFilter,
    }
  );

  // Get claim IDs to fetch their claim items
  const claimIds = useMemo(() => stoplossClaims.map((claim) => claim.id).filter(Boolean), [stoplossClaims]);

  // Fetch claim items for all claims
  const { data: claimItems = [], isLoading: claimItemsLoading } = useGetList<ClaimItem>(
    ResourceName.CLAIM_ITEMS,
    {
      pagination: { page: 1, perPage: 10000 },
      filter: { stoploss_claim_id_ref: claimIds },
    },
    { enabled: claimIds.length > 0 }
  );

  const { data: reimbursements = [] } = useGetList<Reimbursement>(ResourceName.REIMBURSEMENTS, {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: "created_datetime", order: "DESC" },
    filter: baseFilter,
  });

  // Collect unique claimant IDs from both claims and reimbursements for name lookup
  const claimantIds = useMemo(() => {
    const ids = new Set<string>();
    stoplossClaims.forEach((c) => {
      if (c.claimant_id_ref) ids.add(c.claimant_id_ref);
    });
    reimbursements.forEach((r) => {
      if (r.claimant_id_ref) ids.add(r.claimant_id_ref);
    });
    return [...ids];
  }, [stoplossClaims, reimbursements]);

  const { data: claimants = [] } = useGetMany<Claimant>(
    ResourceName.CLAIMANTS,
    {
      ids: claimantIds,
    },
    { enabled: claimantIds.length > 0 }
  );

  const claimantMap = useMemo(() => new Map(claimants.map((c) => [c.id, c.claimant_name])), [claimants]);

  // Calculate financial stats
  const financialStats = useMemo(() => {
    // Still need to calculate IN_PROCESS from claims since backend doesn't provide this breakdown
    const inProcess = stoplossClaims.reduce((acc, claim: StoplossClaim) => {
      if ((IN_PROCESS_STATUSES as readonly string[]).includes(claim.claim_status)) {
        return acc + (claim.total_paid_amount || 0);
      }
      return acc;
    }, 0);

    const submitted = stoplossClaims.reduce((acc, claim: StoplossClaim) => {
      if ((SUBMITTED_STATUSES as readonly string[]).includes(claim.claim_status)) {
        return acc + (claim.total_paid_amount || 0);
      }
      return acc;
    }, 0);

    // Still need to calculate pended from reimbursements since backend doesn't provide this
    const pended = reimbursements.reduce((acc, reimb: Reimbursement) => acc + (Number(reimb.pended_amount) || 0), 0);

    const denied = reimbursements.reduce((acc, reimb: Reimbursement) => acc + (Number(reimb.denial_amount) || 0), 0);

    const reimbursed = reimbursements.reduce(
      (acc, reimb: Reimbursement) => acc + (Number(reimb.reimbursement_amount) || 0),
      0
    );

    // Calculate outstanding amount
    const outstanding = inProcess + submitted - (pended + reimbursed + denied);

    return {
      submitted,
      inProcess,
      denied,
      pended,
      reimbursed,
      outstanding,
    };
  }, [stoplossClaims, reimbursements, clientRecord?.calculations]);

  // Build filter URLs — include policy scope if selected
  const submittedClaimsUrl = `/stoploss-claims?filter=${encodeURIComponent(
    JSON.stringify({ ...baseFilter, claim_status: SUBMITTED_STATUSES })
  )}`;
  const inProcessClaimsUrl = `/stoploss-claims?filter=${encodeURIComponent(
    JSON.stringify({ ...baseFilter, claim_status: IN_PROCESS_STATUSES })
  )}`;
  const allStoplossClaimsUrl = `/stoploss-claims?filter=${encodeURIComponent(JSON.stringify(baseFilter))}`;
  const pendedReimbursementsUrl = `/reimbursements?filter=${encodeURIComponent(
    JSON.stringify({ ...baseFilter, pended_amount_gt: 0 })
  )}`;
  const deniedReimbursementsUrl = `/reimbursements?filter=${encodeURIComponent(
    JSON.stringify({ ...baseFilter, denial_amount_gt: 0 })
  )}`;
  const allReimbursementsUrl = `/reimbursements?filter=${encodeURIComponent(JSON.stringify(baseFilter))}`;
  const claimantsUrl = `/claimants?filter=${encodeURIComponent(JSON.stringify(baseFilter))}`;

  // Enrich data with claimant names for compact tables
  const enrichedClaims = useMemo(
    () => stoplossClaims.map((c) => ({ ...c, claimant_name: claimantMap.get(c.claimant_id_ref) || "" })),
    [stoplossClaims, claimantMap]
  );
  const enrichedReimbursements = useMemo(
    () => reimbursements.map((r) => ({ ...r, claimant_name: claimantMap.get(r.claimant_id_ref) || "" })),
    [reimbursements, claimantMap]
  );

  const isChartsLoading = claimsLoading || claimItemsLoading;

  return (
    <TabContent>
      {/* Financial Stats */}
      <MetricCardsGrid columns={3}>
        <MetricCard
          icon={<StopLossClaimsIcon sx={{ fontSize: 18, color: "primary.main" }} />}
          title="Stop-Loss Claims"
          value={formatUSD(financialStats.submitted + financialStats.inProcess)}
          link={canListClaims ? allStoplossClaimsUrl : undefined}
          subMetrics={[
            {
              label: STOPLOSS_STATUS_GROUP_LABELS.SUBMITTED,
              value: formatUSD(financialStats.submitted),
              link: canListClaims ? submittedClaimsUrl : undefined,
            },
            {
              label: STOPLOSS_STATUS_GROUP_LABELS.IN_PROCESS,
              value: formatUSD(financialStats.inProcess),
              link: canListClaims ? inProcessClaimsUrl : undefined,
            },
            { label: "Total", value: String(stoplossClaims.length) },
          ]}
        />

        <MetricCard
          icon={<ReimbursementIcon sx={{ fontSize: 18, color: "primary.main" }} />}
          title="Reimbursements"
          value={formatUSD(financialStats.reimbursed)}
          link={canListReimbursements ? allReimbursementsUrl : undefined}
          subMetrics={[
            {
              label: "Pended",
              value: formatUSD(financialStats.pended),
              link: canListReimbursements ? pendedReimbursementsUrl : undefined,
            },
            {
              label: "Denied",
              value: formatUSD(financialStats.denied),
              link: canListReimbursements ? deniedReimbursementsUrl : undefined,
            },
            { label: "Total", value: String(reimbursements.length) },
          ]}
        />

        <MetricCard
          icon={<ClaimantsIcon sx={{ fontSize: 18, color: "primary.main" }} />}
          title="Claimants"
          value={totalClaimants || 0}
          link={canListClaimants ? claimantsUrl : undefined}
          subMetrics={[
            {
              label: "Outstanding",
              value: formatUSD(financialStats.outstanding),
              color: financialStats.outstanding > 0 ? "warning.main" : undefined,
            },
          ]}
        />
      </MetricCardsGrid>

      {/* Charts Row */}
      <Box mt={3}>
        <SectionCard title="Claims Activity Over Time" subtitle="Total paid amounts over time" compact>
          <ClaimsOverTimeChart claimItems={isChartsLoading ? [] : claimItems} />
        </SectionCard>
      </Box>

      {/* Lists */}
      <Grid container spacing={3} mt={2} pb={4}>
        {canListStoplossClaims && (
          <Grid size={{ xs: 12, sm: 12, md: drawerExpanded ? 12 : 6 }}>
            <Box>
              <Typography
                variant="overline"
                sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.secondary", display: "block", mb: 1 }}
              >
                Stop-Loss Claims
              </Typography>
              <CompactTable
                data={enrichedClaims}
                columns={stoplossClaimColumns}
                rowLink={(claim) => `/stoploss-claims/${claim.id}/show`}
                rowsPerPage={10}
                emptyMessage="No stop-loss claims"
              />
            </Box>
          </Grid>
        )}

        {canListReimbursements && (
          <Grid size={{ xs: 12, sm: 12, md: drawerExpanded ? 12 : 6 }}>
            <Box>
              <Typography
                variant="overline"
                sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.secondary", display: "block", mb: 1 }}
              >
                Reimbursements
              </Typography>
              <CompactTable
                data={enrichedReimbursements}
                columns={reimbursementColumns}
                rowLink={(reimb) => `/reimbursements/${reimb.id}/show`}
                rowsPerPage={10}
                emptyMessage="No reimbursements"
              />
            </Box>
          </Grid>
        )}
      </Grid>
    </TabContent>
  );
};

// Column definitions imported from @/components/common/reportingColumns
